import { captureException } from '@sentry/nextjs';
import { API_URL } from 'constants/api';
import {
  EngagementBeaconRequestPayload,
  EngagementEventInput,
  EngagementEventType,
  EngagementSource,
  EngagementEntityType,
  ENGAGEMENT_BEACON_API,
  ENGAGEMENT_BEACON_MAX_EVENTS,
  ENGAGEMENT_SOURCE_PARAM,
} from 'constants/payloads/club/engagementBeacon';
import { getViewerToken } from 'services/client/token';

// Stable per-browser anonymous id. No existing mechanism fits: ViewerContext only
// exposes `isAnonymousSession` (no id), and PostHog's distinct_id is production-only
// (absent on staging/dev). Key follows the app's `bounce_` localStorage convention.
const SESSION_STORAGE_KEY = 'bounce_anon_session_id';
const FLUSH_DEBOUNCE_MS = 4000;

let queue: EngagementEventInput[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;
// Per-page-session impression dedup ("entityType:entityId").
const seenImpressions = new Set<string>();

const isBrowser = () => typeof window !== 'undefined';

function getAnonSessionId(): string | null {
  if (!isBrowser()) return null;
  try {
    let id = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return null; // storage blocked (private mode) — events still send anonymously
  }
}

async function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!isBrowser() || queue.length === 0) return;

  const events = queue;
  queue = [];

  const payload: EngagementBeaconRequestPayload = {
    anonSessionId: getAnonSessionId(),
    events,
  };

  try {
    const token = await getViewerToken();
    await fetch(`${API_URL}/${ENGAGEMENT_BEACON_API}`, {
      method: 'POST',
      keepalive: true, // survives page unload, like sendBeacon, but lets us set the auth header
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // Best-effort analytics — never retry or block the page on a failed beacon.
    // Still report to Sentry so silent ingest failures are visible.
    captureException(error, {
      tags: { feature: 'engagement-beacon' },
      extra: { eventCount: events.length },
    });
  }
}

function bindLifecycleFlush() {
  if (listenersBound || !isBrowser()) return;
  listenersBound = true;
  const flushNow = () => {
    void flush();
  };
  // Flush the last batch as the page goes away / is backgrounded.
  window.addEventListener('pagehide', flushNow);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushNow();
  });
}

function enqueue(event: EngagementEventInput) {
  if (!isBrowser()) return;
  bindLifecycleFlush();
  queue.push(event);

  if (queue.length >= ENGAGEMENT_BEACON_MAX_EVENTS) {
    void flush();
    return;
  }
  if (!flushTimer) {
    flushTimer = setTimeout(() => void flush(), FLUSH_DEBOUNCE_MS);
  }
}

interface TrackParams {
  entityType: EngagementEntityType;
  entityId: string;
  source: EngagementSource;
  placementId?: string | null;
}

function track(eventType: EngagementEventType, params: TrackParams) {
  enqueue({ ...params, eventType, occurredAt: new Date().toISOString() });
}

// Exhaustive over EngagementSource — adding an enum value forces an entry here,
// keeping the runtime allow-list in lockstep with the generated enum.
const VALID_SOURCES: Record<EngagementSource, true> = {
  SEARCH: true,
  BROWSE: true,
  CLUB_PAGE: true,
  SHARED_LINK: true,
  BOOST: true,
  OTHER: true,
};

/**
 * Coerce an untrusted source (e.g. a `?src=` query value) to a valid
 * `EngagementSource`, defaulting to `OTHER` for missing/unknown values.
 */
export function resolveEngagementSource(raw: unknown): EngagementSource {
  if (typeof raw === 'string') {
    const upper = raw.toUpperCase();
    if (upper in VALID_SOURCES) return upper as EngagementSource;
  }
  return 'OTHER';
}

/**
 * Read the engagement source for the current page from the `?src=` query param.
 * Reads `window.location` directly so it isn't subject to Next router hydration
 * timing. Returns `OTHER` on the server or for direct/unknown navigations.
 */
export function getEngagementSourceFromUrl(): EngagementSource {
  if (!isBrowser()) return 'OTHER';
  const raw = new URLSearchParams(window.location.search).get(ENGAGEMENT_SOURCE_PARAM);
  return resolveEngagementSource(raw);
}

/** Card seen in a feed/search list (deduped once per page session per entity). */
export function trackImpression(params: TrackParams) {
  const key = `${params.entityType}:${params.entityId}`;
  if (seenImpressions.has(key)) return;
  seenImpressions.add(key);
  track('IMPRESSION', params);
}

/** Detail page opened. */
export function trackView(params: TrackParams) {
  track('VIEW', params);
}

/** Join/Register CTA clicked. */
export function trackCtaClick(params: TrackParams) {
  track('CTA_CLICK', params);
}
